import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import api from './lib/api';
import { ToastProvider } from './contexts/ToastContext';
import LandingPage from './components/landing/LandingPage';
import AuthPage from './components/auth/AuthPage';
import WelcomeModal from './components/onboarding/WelcomeModal';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import JobsPage from './components/jobs/JobsPage';
import CandidatesPage from './components/candidates/CandidatesPage';
import CandidateDetailPage from './components/candidates/CandidateDetailPage';
import AgentsPage from './components/agents/AgentsPage';
import ScreeningPage from './components/screening/ScreeningPage';
import CallsPage from './components/calls/CallsPage';
import TeamPage from './components/team/TeamPage';
import PublicSchedulePage from './components/scheduling/PublicSchedulePage';
import InviteAcceptPage from './components/team/InviteAcceptPage';
import ReportsPage from './components/reports/ReportsPage';
import SettingsPage from './components/settings/SettingsPage';
import AnalyticsPage from './components/analytics/AnalyticsPage';
import VoiceScreeningPage from './components/voice/VoiceScreeningPage';
import VoiceDashboard from './pages/VoiceDashboard';
import VoiceCallDetail from './pages/VoiceCallDetail';
import SourcingAgentsPage from './components/sourcing/SourcingAgentsPage';
import ApplyPage from './pages/ApplyPage';
import ProductivityDashboard from './pages/ProductivityDashboard';
import { Mic } from 'lucide-react';

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWsId, setActiveWsId] = useState(localStorage.getItem('vocala_workspace_id'));
  const [showLanding, setShowLanding] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setWorkspaces(data.workspaces || []);
      setShowLanding(false);

      // Set active workspace
      const savedWsId = localStorage.getItem('vocala_workspace_id');
      if (savedWsId && data.workspaces?.some(w => w.workspace_id === savedWsId)) {
        setActiveWsId(savedWsId);
        const ws = data.workspaces.find(w => w.workspace_id === savedWsId);
        setCurrentWorkspace(ws);
      } else if (data.active_workspace_id) {
        setActiveWsId(data.active_workspace_id);
        localStorage.setItem('vocala_workspace_id', data.active_workspace_id);
        const ws = data.workspaces?.find(w => w.workspace_id === data.active_workspace_id);
        setCurrentWorkspace(ws);
      } else if (data.workspaces?.length > 0) {
        const firstId = data.workspaces[0].workspace_id;
        setActiveWsId(firstId);
        localStorage.setItem('vocala_workspace_id', firstId);
        setCurrentWorkspace(data.workspaces[0]);
      }

      // Auto-accept pending invite
      const pendingInvite = localStorage.getItem('vocala_pending_invite');
      if (pendingInvite) {
        localStorage.removeItem('vocala_pending_invite');
        try {
          await api.post(`/workspaces/accept-invite/${pendingInvite}`);
          const { data: refreshed } = await api.get('/auth/me');
          setUser(refreshed);
          setWorkspaces(refreshed.workspaces || []);
        } catch (e) { logger.error('Auto-accept invite failed:', e); }
      }
    } catch (err) {
      localStorage.removeItem('vocala_token');
      localStorage.removeItem('vocala_workspace_id');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('vocala_token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const handleLogin = () => {
    setShowLanding(false);
    fetchUser();
  };

  const handleLogout = () => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('vocala_token');
    localStorage.removeItem('vocala_workspace_id');
    setUser(null);
    setWorkspaces([]);
    setShowLanding(true);
  };

  const handleSwitchWorkspace = (wsId) => {
    setActiveWsId(wsId);
    localStorage.setItem('vocala_workspace_id', wsId);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center relative overflow-hidden" data-testid="app-loading" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-400/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="flex flex-col items-center gap-6 relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500 rounded-2xl animate-ping opacity-20" />
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1e40af] to-blue-500 flex items-center justify-center shadow-2xl relative z-10">
              <Mic className="w-8 h-8 text-white animate-pulse" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-slate-800 text-xl font-bold tracking-tight mb-1">Vocala<span className="text-blue-500">.</span></p>
            <p className="text-slate-400 text-sm font-medium animate-pulse">Waking up agents...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showLanding && !user) {
    return <LandingPage onGetStarted={() => setShowLanding(false)} />;
  }

  if (!user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  return (
    <div className="flex h-screen bg-[#f8fafc] overflow-hidden relative" data-testid="app-layout" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-400/10 rounded-full blur-[120px] pointer-events-none mix-blend-multiply transition-all duration-1000" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-400/10 rounded-full blur-[120px] pointer-events-none mix-blend-multiply transition-all duration-1000" />

      <WelcomeModal user={user} workspace={currentWorkspace} />

      <Sidebar
        user={user}
        onLogout={handleLogout}
        workspaces={workspaces}
        activeWsId={activeWsId}
        onSwitchWorkspace={handleSwitchWorkspace}
      />

      <main className="flex-1 overflow-auto bg-transparent relative z-10 scroll-smooth">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:jobId/candidates" element={<CandidatesPage />} />
          <Route path="/candidates" element={<CandidatesPage />} />
          <Route path="/candidates/:candidateId" element={<CandidateDetailPage />} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/sourcing-agents" element={<SourcingAgentsPage />} />
          <Route path="/screening/:callId" element={<ScreeningPage />} />
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/voice" element={<VoiceDashboard />} />
          <Route path="/voice/calls/:id" element={<VoiceCallDetail />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/productivity" element={<ProductivityDashboard />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <Routes>
          {/* Public routes — no auth required */}
          <Route path="/schedule/:token" element={<PublicSchedulePage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/apply/:jobId" element={<ApplyPage />} />
          {/* App routes */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
